import type { ExpenseListQuery } from '@chukua/common';
import type { TableColumnsType } from 'antd';
import { Paperclip } from 'lucide-react';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { formatMoney } from '../../../shared/policies/format-money';
import type { ExpenseCategoryView, ExpenseView } from '../expenses.api';
import { waitingFor } from '../policies/expense-age';
import type { DecisionMode } from '../policies/expense-decision';
import { ExpenseDecisionButtons } from './ExpenseDecisionButtons';

interface BuildApprovalColumnsArgs {
  list: Pick<ListQueryControls<ExpenseListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  categories: ExpenseCategoryView[];
  now: Date;
  onDecide: (mode: DecisionMode, expense: ExpenseView) => void;
}

/** Each column's `key` is its `sort` field or filter param in `expenseListQuerySchema`, so sorting and filtering run on the server. */
export function buildApprovalColumns({ list, categories, now, onDecide }: BuildApprovalColumnsArgs): TableColumnsType<ExpenseView> {
  return [
    {
      title: 'Waiting',
      key: 'serverTime',
      sorter: true,
      sortOrder: list.sortOrderFor('serverTime'),
      render: (_, expense) => waitingFor(expense.serverTime, now),
    },
    { title: 'Driver', dataIndex: 'driverName', key: 'driver' },
    {
      title: 'For',
      key: 'subject',
      render: (_, expense) => (expense.truckRegistration ? `Truck ${expense.truckRegistration}` : `Trip ${expense.tripNumber ?? ''}`.trim()),
    },
    {
      title: 'Category',
      key: 'category',
      sorter: true,
      sortOrder: list.sortOrderFor('category'),
      filters: categories.map((category) => ({ text: category.name, value: category.code })),
      filteredValue: list.filteredValueFor('category'),
      render: (_, expense) => expense.category.name,
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      sorter: true,
      sortOrder: list.sortOrderFor('amount'),
      render: (_, expense) => formatMoney(expense.amount),
    },
    {
      title: 'Receipts',
      key: 'receipts',
      render: (_, expense) => (
        <span className="inline-flex items-center gap-1 text-muted">
          <Paperclip className="size-4" aria-hidden />
          {expense.receipts.length}
        </span>
      ),
    },
    { title: 'Decide', key: 'decide', render: (_, expense) => <ExpenseDecisionButtons expense={expense} onDecide={onDecide} /> },
  ];
}
